import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import '../../college-theme.css';
import { Avatar, DeskButton, DeskEmpty, Eyebrow } from './essayShared';

const ROLE_TAGS = { PARENT: 'Parent', ADVISOR: 'Advisor' };

// Where somebody lands when more than one student has shared a Desk with them.
// A single share forwards straight through to that Desk.
export default function SharedDeskIndexPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .get('/college-access/shared-with-me')
      .then((res) => {
        if (!alive) return;
        const list = res.data.shares || [];
        if (list.length === 1) {
          navigate(`/shared/${list[0].id}`, { replace: true });
          return;
        }
        setShares(list);
      })
      .catch(() => {
        if (alive) toast.error('Failed to load what has been shared with you');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [api, navigate]);

  return (
    <div className="college-scope" style={{ minHeight: '100vh', padding: '56px 32px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <Eyebrow>Shared with you</Eyebrow>
        <h1
          className="font-serif-editorial"
          style={{ fontSize: 44, fontWeight: 400, lineHeight: 1, margin: '0 0 28px', color: 'var(--college-ink)' }}
        >
          Their desks
        </h1>

        {loading ? (
          <p className="font-mono-editorial" style={{ fontSize: 13, color: 'var(--college-muted)' }}>
            Loading…
          </p>
        ) : shares.length === 0 ? (
          <DeskEmpty
            title="Nothing shared with you yet"
            body="When a student invites you to their College Desk, it turns up here. Invite links expire after seven days, so ask for a fresh one if yours has gone stale."
            action={<DeskButton onClick={() => navigate('/hub')}>Back to your hub</DeskButton>}
          />
        ) : (
          shares.map((share) => (
            <button
              key={share.id}
              type="button"
              onClick={() => navigate(`/shared/${share.id}`)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                width: '100%',
                textAlign: 'left',
                padding: '16px 18px',
                marginBottom: 12,
                background: 'var(--college-panel)',
                border: '1px solid var(--college-border-soft)',
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              <Avatar person={{ kind: 'grant', id: share.id, name: share.ownerName, role: share.role }} size={36} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <span className="font-serif-editorial" style={{ fontSize: 19, color: 'var(--college-ink)' }}>
                  {share.ownerName}
                </span>
                <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--college-muted)' }}>
                  You're {ROLE_TAGS[share.role]?.toLowerCase()} here ·{' '}
                  {[share.seeProgress && 'progress', share.readDrafts && 'drafts', share.comment && 'comments']
                    .filter(Boolean)
                    .join(' · ') || 'view only'}
                </p>
              </div>
              <span style={{ fontSize: 12, color: 'var(--college-red)' }}>Open →</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
